import { useEffect, useRef } from 'react'

interface UseCanvasDuplicateShortcutOptions {
  /** Nur im Maus-Werkzeug des sichtbaren Tabs (TabStack-Falle). */
  enabled: boolean
  /**
   * Das Canvas-Element, an dem gehorcht wird — NICHT das `window`.
   * `null`, solange React Flow seine Pane noch nicht aufgebaut hat.
   */
  container: HTMLElement | null
  /**
   * @param fromNoteId gesetzt, wenn der Druck aus dem TEXT eines Zettels kam —
   *   dann ist dieser gemeint, nicht die Auswahl (siehe unten).
   */
  onDuplicate: (fromNoteId?: string) => void
}

/**
 * ⌘/Strg + D dupliziert die ausgewählten **Notizen**.
 *
 * **Warum am Canvas-Element und nicht am `window`:** Dieses Kürzel gibt es
 * schon — `useProjectKeyboardShortcuts` dupliziert damit ausgewählte Aufgaben,
 * und zwar über einen Listener am `window`, der nur auf den sichtbaren Tab
 * beschränkt ist, nicht auf die Aufgabenliste. Bei offenem Canvas feuerten
 * sonst beide.
 *
 * Über die Reihenfolge ist das nicht zu lösen: Das Panel wird erst beim Öffnen
 * aufgebaut, sein Listener käme also nach dem der Seite —
 * `stopImmediatePropagation` von hier aus käme zu spät. Am Canvas-Element
 * dagegen läuft der Tastendruck auf dem Weg nach oben ZUERST hier durch;
 * `stopPropagation()` hält ihn auf, bevor das `window` ihn sieht, unabhängig
 * davon, wer sich wann registriert hat.
 *
 * Damit ist zugleich die Vorrangfrage beantwortet, und zwar mit der Regel, die
 * man ohnehin erwartet: Es zählt, wo der Fokus liegt. Wer eine Notiz
 * anklickt, fokussiert React Flows Node-Hülle; wer einen Auswahlrahmen zieht,
 * das Auswahlrechteck. Beides liegt im Canvas. Wer danach in die Aufgabenliste
 * klickt, nimmt den Fokus mit — dann dupliziert ⌘D wieder Aufgaben.
 *
 * Aufgehalten wird nur, was wir auch verarbeiten: Bei jeder anderen Taste läuft
 * das Ereignis unangetastet weiter.
 *
 * **Im Bearbeiten-Modus gilt es dem Zettel, in dem getippt wird** (gewünscht
 * 2026-09-05) — genau wie ⇥ dort einen neuen daneben legt, statt durch die
 * Reihenfolge zu springen. Wer einen frischen Notizen absetzt und lostippt,
 * hat keine Auswahl im herkömmlichen Sinn; die Kennung kommt deshalb aus dem
 * Feld. Ein Feld AUSSERHALB eines Zettels behält sein ⌘D.
 *
 * @example
 * useCanvasDuplicateShortcut({ enabled, container: domNode, onDuplicate })
 */
export function useCanvasDuplicateShortcut({
  enabled,
  container,
  onDuplicate,
}: UseCanvasDuplicateShortcutOptions) {
  // Über ein Ref, damit der Listener nicht bei jeder Änderung an der Auswahl
  // neu gebunden wird — während eines Ziehvorgangs wäre das jeder Frame.
  const onDuplicateRef = useRef(onDuplicate)
  onDuplicateRef.current = onDuplicate

  useEffect(() => {
    if (!enabled || !container) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      // ⇧⌘D und ⌥⌘D gehören uns nicht.
      if (e.shiftKey || e.altKey) return
      // `code` statt `key`, damit das Kürzel auf jedem Layout dort liegt, wo es
      // eingezeichnet ist.
      if (e.code !== 'KeyD') return
      // Ein gehaltenes ⌘D soll NICHT wiederholen: bei ~30 Anschlägen pro
      // Sekunde und einem langsameren Dexie-Roundtrip entstünde ein Stapel
      // deckungsgleicher Kopien, den man nicht einmal sieht.
      if (e.repeat) return
      // Einem offenen Menü seine Tasten lassen.
      if (document.querySelector('[role="menu"]')) return
      /*
        Wird gerade getippt? `readOnly` zählt mit und nicht bloß der Tag-Name:
        Der `NoteNode` rendert sein Textfeld immer und schützt es nur,
        solange nicht bearbeitet wird — ein ruhendes Feld nimmt weiterhin
        Fokus an (dieselbe Falle wie in `useCanvasTabShortcut`).
      */
      const target = e.target as HTMLElement | null
      const typing = (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement)
        && !target.readOnly
      // Ein fremdes Feld im Canvas behält sein ⌘D — gemeint sind nur die
      // Textfelder der Zettel.
      const fromNoteId = typing
        ? target.closest<HTMLElement>('.react-flow__node')?.dataset.id
        : undefined
      if (typing && !fromNoteId) return

      e.preventDefault()
      // Hält die Aufgabenliste ab (siehe oben). Im Web verhindert
      // `preventDefault` zugleich das Lesezeichen.
      e.stopPropagation()
      onDuplicateRef.current(fromNoteId)
    }

    container.addEventListener('keydown', handleKeyDown)
    return () => container.removeEventListener('keydown', handleKeyDown)
  }, [enabled, container])
}
